/**
 * Everything the client report shows that `POST portfolio_planner/generate/`
 * does not return.
 *
 * The API hands back the plan itself — segments, future costs, gaps, SIPs,
 * baskets and the every-rupee trace. The report additionally shows how much of
 * each goal is contribution versus growth, how the plan holds up if returns
 * come in lower, what deferring the start costs, and how exposure splits across
 * fund houses. None of that is in the response, so it is derived here from the
 * PlanResult plus the same TVM maths the engine used.
 *
 * Every figure below was checked against backend's own sample report
 * ("MF Portfolio Calculator/Sample generated report.pdf") and reproduces it:
 * contributed ₹3,69,693, capital 1.35×, return share 26%, direct-fund-would-be
 * ₹84,253, deferral ₹7,067 / ₹9,987.
 *
 * If backend later adds any of this to `summary` or `assumptions`, prefer the
 * server's number and delete the local one — two sources for one figure is how
 * a report starts contradicting itself.
 */

import {
  fvLumpsum,
  fvSIP,
  instalments,
  monthlyEquivalent,
  sipForTarget,
} from "./tvm";
import type {
  PlanResult,
  PlannerGoalResult,
  PlannerLiabilityResult,
} from "./types";

/** Return shocks modelled on the sensitivity table, in percentage points. */
export const SENSITIVITY_DELTAS = [0, 1, 2, 3] as const;

/** Start-date deferrals modelled on the deferral table, in years. */
export const DEFERRAL_YEARS = [0, 1, 2, 3] as const;

export interface SensitivityRow {
  /** Percentage points shaved off every expected return. 0 = baseline. */
  deltaPct: number;
  label: string;
  /** Corpus at the goal date under the shocked return. */
  corpus: number;
  /** Corpus as a share of the target, capped at 100 for display honesty. */
  fundedPct: number;
  comment: string;
}

export interface DeferralRow {
  deferYears: number;
  label: string;
  /** Instalment needed to still land the same target on the same date. */
  sip: number;
  /** Increase over starting now; 0 on the baseline row. */
  change: number;
  /** False once the deferral eats the whole timeline. */
  reachable: boolean;
}

export interface GoalAnalytics {
  goalId: number;
  /** Instalments actually paid in over the tenure, plus any lumpsum deployed. */
  contributed: number;
  /** Target minus contributed — the part compounding delivers. */
  growth: number;
  /**
   * What the plan actually projects this goal to be worth on its date, which
   * is not the same as the target: an over-funded goal delivers more. Used for
   * "wealth created", because measuring growth against the target reports zero
   * on a plan that turned ₹10L into ₹16.8L.
   */
  delivered: number;
  /** Growth as a share of the target. */
  returnSharePct: number;
  /** Target ÷ contributed. "1.35×" on the report. */
  capitalMultiple: number;
  /** Future cost ÷ today's cost. "1.0×" when inflation is zero. */
  inflationMultiple: number;
  instalmentCount: number;
  /** The goal's SIP expressed monthly, whatever its frequency. */
  monthlySip: number;
  /**
   * What the same lumpsum would have delivered as a plain mutual-fund one-time
   * investment — the honest comparison against the bond route. Null unless this
   * goal actually took the bond route.
   */
  directFundWouldBe: number | null;
  sensitivity: SensitivityRow[];
  deferral: DeferralRow[];
  /** Year-end projected corpus against the flat target, for the growth chart. */
  series: { year: number; corpus: number; target: number }[];
}

export interface ExposureRow {
  name: string;
  amount: number;
  sharePct: number;
}

export interface CashflowLine {
  label: string;
  amount: number;
  /** Rendered as a deduction. */
  isDeduction?: boolean;
  isTotal?: boolean;
}

export interface PlanAnalytics {
  goals: GoalAnalytics[];
  byGoalId: Record<number, GoalAnalytics>;
  /** Total paid in across every goal, including the lumpsum. */
  contributed: number;
  /** Total target minus total contributed. */
  wealthCreated: number;
  /** Monthly commitment as a share of the declared surplus. */
  sipSharePct: number;
  /** Monthly SIP grouped by fund house, largest first, against the 20% cap. */
  amcExposure: ExposureRow[];
  /** True when any fund house is over the cap, or the server said so. */
  amcCapBreached: boolean;
  /** Monthly SIP grouped by asset segment. */
  segmentSplit: ExposureRow[];
  monthlyCashflow: CashflowLine[];
  annualCashflow: CashflowLine[];
  /** Savings rate on the declared surplus. */
  savingsRatePct: number;
  /** Whole-plan funded % under each return shock. */
  planSensitivity: SensitivityRow[];
  planDeferral: PlanDeferralRow[];
}

export interface PlanDeferralRow extends Omit<DeferralRow, "change"> {
  reachableCount: number;
  totalGoals: number;
  /**
   * Objectives whose whole timeline the deferral consumes. They can no longer
   * be funded at all, so they contribute nothing to `sip` — which means a row
   * with dropouts is NOT comparable with the baseline. `change` is null there
   * for exactly that reason: a smaller basket costing less per month is not a
   * saving, it is a missed goal.
   */
  droppedCount: number;
  /** Null when the reachable set differs from the baseline's. */
  change: number | null;
}

const safeDivide = (numerator: number, denominator: number): number =>
  denominator > 0 ? numerator / denominator : 0;

/**
 * A goal's tenure, in months and in fractional years.
 *
 * `goal.years` and `goal.months` are a SPLIT, not two ways of saying the same
 * thing: a 4y 5m goal returns years 4 and months 5, and `years * 12` is 48 when
 * the answer is 53. Reading `years` alone dropped five instalments off every
 * count on that plan and shortened every compounding period by five months.
 * `totalMonths` is the only field that carries the whole tenure.
 */
const tenureOf = (goal: PlannerGoalResult): { months: number; years: number } => {
  const months = Math.round(
    goal.totalMonths ?? goal.years * 12 + (goal.months ?? 0)
  );
  return { months, years: months / 12 };
};

const sensitivityComment = (fundedPct: number, isBaseline: boolean): string => {
  if (isBaseline) return "baseline";
  if (fundedPct >= 90) return "resilient";
  if (fundedPct >= 75) return "manageable shortfall";
  return "material shortfall";
};

/**
 * Corpus a goal lands on if every expected return is `deltaPct` points lower.
 *
 * The SIP leg and the mutual-fund lumpsum leg both compound at the goal's
 * return, so both move. On the bond route only part of the lumpsum is
 * return-driven: the swept coupons and the idle cash compound at the goal
 * return, while the bond principal is parked at the fixed debt rate and the
 * bond's own coupons are contractual. So the fixed leg is held still, which is
 * what makes the bond route look more defensive here — because it is.
 */
const shockedCorpus = (goal: PlannerGoalResult, deltaPct: number): number => {
  const shockedReturn = Math.max(0, goal.ret - deltaPct);
  const { months, years } = tenureOf(goal);
  const sipLeg = fvSIP(goal.sip, shockedReturn, months, goal.sipFreq);

  if (!goal.coveredByLumpsum) return sipLeg;

  if (goal.bond) {
    const returnDriven = goal.bond.couponCorpusAtGoal + goal.bond.idleAtGoal;
    const fixed = goal.bond.principalAtGoal;
    const scale = safeDivide(
      Math.pow(1 + shockedReturn / 100, years),
      Math.pow(1 + goal.ret / 100, years)
    );
    return sipLeg + returnDriven * scale + fixed;
  }

  // Plain mutual-fund lumpsum: re-grow it from today at the shocked return.
  const scale = safeDivide(
    Math.pow(1 + shockedReturn / 100, years),
    Math.pow(1 + goal.ret / 100, years)
  );
  return sipLeg + goal.coveredByLumpsum * scale;
};

const buildGoalAnalytics = (
  goal: PlannerGoalResult,
  planLumpsum: number
): GoalAnalytics => {
  const { months, years } = tenureOf(goal);
  const instalmentCount = instalments(months, goal.sipFreq);
  const lumpsumDeployed = goal.usedLumpsum ? planLumpsum : 0;
  const contributed = goal.sip * instalmentCount + lumpsumDeployed;
  const growth = Math.max(0, goal.futureCost - contributed);

  const sensitivity: SensitivityRow[] = SENSITIVITY_DELTAS.map((deltaPct) => {
    const corpus = shockedCorpus(goal, deltaPct);
    const fundedPct = Math.min(100, safeDivide(corpus, goal.futureCost) * 100);
    return {
      deltaPct,
      label: deltaPct === 0 ? "As assumed" : `−${deltaPct}% p.a.`,
      corpus,
      fundedPct,
      comment: sensitivityComment(fundedPct, deltaPct === 0),
    };
  });

  const baselineSip = goal.sip;
  const deferral: DeferralRow[] = DEFERRAL_YEARS.map((deferYears) => {
    const remainingMonths = months - deferYears * 12;
    const reachable = remainingMonths > 0;
    // The lumpsum is deployed today either way, so only the timeline shortens:
    // the same gap has to be closed over fewer instalments.
    const sip = reachable
      ? sipForTarget(goal.gap, goal.ret, remainingMonths, goal.sipFreq)
      : 0;
    return {
      deferYears,
      label: deferYears === 0 ? "Start now" : `Defer ${deferYears} yr`,
      sip,
      change: deferYears === 0 ? 0 : sip - baselineSip,
      reachable,
    };
  });

  const wholeYears = Math.max(1, Math.round(years));
  const series = Array.from({ length: wholeYears + 1 }, (_, year) => ({
    year,
    corpus: Math.round(
      fvSIP(goal.sip, goal.ret, year * 12, goal.sipFreq) +
        (goal.coveredByLumpsum
          ? goal.coveredByLumpsum / Math.pow(1 + goal.ret / 100, years - year)
          : 0)
    ),
    target: Math.round(goal.futureCost),
  }));

  const delivered =
    goal.coveredByLumpsum + fvSIP(goal.sip, goal.ret, months, goal.sipFreq);

  return {
    goalId: goal.id,
    contributed,
    growth,
    delivered,
    returnSharePct: safeDivide(growth, goal.futureCost) * 100,
    capitalMultiple: safeDivide(goal.futureCost, contributed),
    inflationMultiple: safeDivide(goal.futureCost, goal.costToday),
    instalmentCount,
    monthlySip: monthlyEquivalent(goal.sip, goal.sipFreq),
    directFundWouldBe: goal.bond
      ? fvLumpsum(lumpsumDeployed, goal.ret, years)
      : null,
    sensitivity,
    deferral,
    series,
  };
};

/**
 * A liability's monthly commitment: the 10%-of-EMI SIP, topped up if it falls
 * short or the loan started before the plan. A catch-up loan can cancel and
 * still need its top-up, so this cannot key off `cancels`.
 */
export const liabilityMonthlySip = (liability: PlannerLiabilityResult): number =>
  Math.max(liability.sip10, liability.sipNeeded);

export const buildPlanAnalytics = (plan: PlanResult): PlanAnalytics => {
  const goals = plan.goals.map((goal) =>
    buildGoalAnalytics(goal, plan.summary.lumpsum)
  );
  const byGoalId = goals.reduce<Record<number, GoalAnalytics>>((map, entry) => {
    map[entry.goalId] = entry;
    return map;
  }, {});

  const contributed = goals.reduce((sum, entry) => sum + entry.contributed, 0);

  /* ── exposure: every basket, goals and liabilities alike ── */
  const amcTotals = new Map<string, number>();
  const segmentTotals = new Map<string, number>();

  plan.goals.forEach((goal) => {
    const factor =
      goal.sip > 0 ? monthlyEquivalent(goal.sip, goal.sipFreq) / goal.sip : 0;
    goal.funds.forEach((fund) => {
      const monthly = fund.amount * factor;
      amcTotals.set(fund.amc, (amcTotals.get(fund.amc) ?? 0) + monthly);
      segmentTotals.set(goal.seg, (segmentTotals.get(goal.seg) ?? 0) + monthly);
    });
  });

  plan.liabilities.forEach((liability) => {
    liability.funds.forEach((fund) => {
      amcTotals.set(fund.amc, (amcTotals.get(fund.amc) ?? 0) + fund.amount);
      segmentTotals.set(
        liability.seg,
        (segmentTotals.get(liability.seg) ?? 0) + fund.amount
      );
    });
  });

  const toExposure = (totals: Map<string, number>): ExposureRow[] => {
    const total = Array.from(totals.values()).reduce((sum, v) => sum + v, 0);
    return Array.from(totals.entries())
      .map(([name, amount]) => ({
        name,
        amount,
        sharePct: safeDivide(amount, total) * 100,
      }))
      .sort((a, b) => b.amount - a.amount);
  };

  const amcExposure = toExposure(amcTotals);
  const serverFlaggedBreach =
    plan.goals.some((goal) => goal.amcCapSatisfied === false) ||
    plan.liabilities.some((liability) => liability.amcCapSatisfied === false);

  /* ── cashflow appendix ── */
  const surplus = plan.summary.monthlySurplus;
  const monthlyCashflow: CashflowLine[] = [
    { label: "Investable surplus declared", amount: surplus },
    ...plan.goals.map((goal, index) => ({
      label: `Less: SIP — ${goal.name} (${goal.seg}, priority ${index + 1})`,
      amount: goals[index].monthlySip,
      isDeduction: true,
    })),
    ...plan.liabilities.map((liability) => ({
      label: `Less: cancel-out SIP — ${liability.name}`,
      amount: liabilityMonthlySip(liability),
      isDeduction: true,
    })),
    {
      label: "Unallocated surplus",
      amount: surplus - plan.summary.totalMonthlySIP,
      isTotal: true,
    },
  ];

  const annualCashflow: CashflowLine[] = [
    { label: "Investable surplus", amount: surplus * 12 },
    { label: "Invested (plan)", amount: plan.summary.totalMonthlySIP * 12 },
    {
      label: "Unallocated",
      amount: (surplus - plan.summary.totalMonthlySIP) * 12,
      isTotal: true,
    },
  ];

  /* ── whole-plan sensitivity and deferral ── */
  const planSensitivity: SensitivityRow[] = SENSITIVITY_DELTAS.map((deltaPct) => {
    const corpus = plan.goals.reduce(
      (sum, goal) => sum + shockedCorpus(goal, deltaPct),
      0
    );
    const fundedPct = Math.min(
      100,
      safeDivide(corpus, plan.summary.totalFutureCost) * 100
    );
    return {
      deltaPct,
      label: deltaPct === 0 ? "As assumed" : `−${deltaPct}% p.a.`,
      corpus,
      fundedPct,
      comment: sensitivityComment(fundedPct, deltaPct === 0),
    };
  });

  const baselineMonthly = plan.goals.reduce(
    (sum, goal, index) => sum + goals[index].monthlySip,
    0
  );
  const planDeferral: PlanDeferralRow[] = DEFERRAL_YEARS.map((deferYears) => {
    let reachableCount = 0;
    const monthly = plan.goals.reduce((sum, goal, index) => {
      const row = goals[index].deferral.find(
        (entry) => entry.deferYears === deferYears
      );
      if (!row?.reachable) return sum;
      reachableCount += 1;
      return sum + monthlyEquivalent(row.sip, goal.sipFreq);
    }, 0);
    const droppedCount = plan.goals.length - reachableCount;
    return {
      deferYears,
      label: deferYears === 0 ? "Start now" : `Defer ${deferYears} yr`,
      sip: monthly,
      // Only comparable while the same objectives are still on the table.
      change: deferYears === 0 ? 0 : droppedCount > 0 ? null : monthly - baselineMonthly,
      reachable: reachableCount > 0,
      reachableCount,
      totalGoals: plan.goals.length,
      droppedCount,
    };
  });

  return {
    goals,
    byGoalId,
    contributed,
    // Measured against what the plan delivers, not against the target — the
    // two are the same on a normally funded plan and differ sharply on an
    // over-funded one, where target-minus-contributed reports nothing gained.
    wealthCreated: Math.max(
      0,
      goals.reduce((sum, entry) => sum + entry.delivered, 0) - contributed
    ),
    sipSharePct: safeDivide(plan.summary.totalMonthlySIP, surplus) * 100,
    amcExposure,
    amcCapBreached:
      serverFlaggedBreach || amcExposure.some((row) => row.sharePct > 20.5),
    segmentSplit: toExposure(segmentTotals),
    monthlyCashflow,
    annualCashflow,
    savingsRatePct: safeDivide(plan.summary.totalMonthlySIP, surplus) * 100,
    planSensitivity,
    planDeferral,
  };
};
